import type { ThreeNS } from "./three";
import { DIST_SCALE } from "./plateField";
import { GAP_SCALE, MAX_PLATES, TIP_LEN, FRONT_SOFT, STATE_TEXELS } from "./shell";
import { IMPRINT_RANGE } from "./imprint";
import * as C from "../engine/constants";

/**
 * Cracks drawn by the ball's own MeshPhysicalMaterial (injected with onBeforeCompile), from:
 *   uFieldId    RG8, nearest  — owner plate, neighbour plate across the closest border
 *   uFieldDist  R8, linear    — distance to that border (field texels × DIST_SCALE)
 *   uFieldFine  RG8, linear   — finer crack layer (a crushed plate's fragments): distance to its border, cell random
 *   uPlateState RGBA8 768×1   — per plate, three texels: hairline, gap half-width, tilt, open | fine crack
 *                               half-width, fine hairline, sink into the clay, crushed | centre (xyz), lift
 *   uRestR      R16F          — the ball's plastic shape: radius − 1 per vertex of the coarse shape grid
 *   uImprint    RGBA8, linear — finger marks in the clay: height high byte, freshly pressed, height low byte
 *   uPlateFront RGBA8 256×1   — per plate: crack origin (xyz) and running front reach (a·π rad):
 *                               a crack only shows within that reach, thinning to a tip, so it runs out from the finger
 *   uHole       R8, linear    — peeled-chip holes (0.5 = hole edge)
 * Every width is compared against fwidth() of the interpolated distance, so edges are anti-aliased
 * at any zoom: no stair-steps, and hairlines never thinner than ~1 px.
 *
 * The vertex shader builds the ball's shape (uShapeOn = 1): the plastic memory, the live dent under the finger
 * with the clay it pushes up around it and the slight swell of the rest of the ball, the squash, and the relief
 * of the shell — broken plates lifted, tipped or pressed in as rigid pieces, crushed fragments each at its own
 * depth, finger marks in the clay. The fragment shader shades the same relief (shReliefAt) at texel resolution.
 *
 * Every ball and every peeled chip uses this same function object, so three gives them one shared
 * program (the cache key is the function source): a chip appearing mid-drag never compiles a shader.
 */

export type CrackUniforms = Record<string, { value: unknown }>;

/** 1×1 placeholders so the sampler uniforms exist from the first frame (no recompile later). */
export function makeDummyTextures(T: ThreeNS) {
  const id = new T.DataTexture(new Uint8Array([0, 0]), 1, 1, T.RGFormat, T.UnsignedByteType);
  const dist = new T.DataTexture(new Uint8Array([255]), 1, 1, T.RedFormat, T.UnsignedByteType);
  const state = new T.DataTexture(new Uint8Array(MAX_PLATES * 4 * STATE_TEXELS), MAX_PLATES * STATE_TEXELS, 1, T.RGBAFormat, T.UnsignedByteType);
  const front = new T.DataTexture(new Uint8Array(MAX_PLATES * 4), MAX_PLATES, 1, T.RGBAFormat, T.UnsignedByteType);
  const hole = new T.DataTexture(new Uint8Array([0]), 1, 1, T.RedFormat, T.UnsignedByteType);
  const fine = new T.DataTexture(new Uint8Array([255, 128]), 1, 1, T.RGFormat, T.UnsignedByteType);
  const rest = new T.DataTexture(new Uint16Array([0]), 1, 1, T.RedFormat, T.HalfFloatType); // r − 1 = 0: round
  const imprint = new T.DataTexture(new Uint8Array([128, 0]), 1, 1, T.RGFormat, T.UnsignedByteType);
  for (const t of [id, dist, state, front, hole, fine, rest, imprint]) { t.needsUpdate = true; }
  rest.magFilter = rest.minFilter = T.LinearFilter;
  return { id, dist, state, front, hole, fine, rest, imprint };
}

export function makeCrackUniforms(T: ThreeNS, dummies: ReturnType<typeof makeDummyTextures>, variant: C.ShellVariant): CrackUniforms {
  const light = variant.light;
  return {
    uCrackOn: { value: 0 },
    uFieldId: { value: dummies.id },
    uFieldDist: { value: dummies.dist },
    uFieldFine: { value: dummies.fine },
    uPlateState: { value: dummies.state },
    uPlateFront: { value: dummies.front },
    uHole: { value: dummies.hole },
    uClay: { value: new T.Color(variant.core) },
    uMudColor: { value: new T.Color(variant.base) },
    uMud: { value: 0 },
    // paraffin whitens where it breaks; on milk/steel shells a cool grey reads better than white
    uEdge: { value: new T.Color(light ? "#7d8598" : C.FRACTURE) },
    uHair: { value: new T.Color(light ? "#6f778a" : "#c3cad8") },
    // the inner, uncoloured wax layer that shows in a broken edge under the coloured coat
    uWax: { value: new T.Color(C.CLAY) },
    uTexelWorld: { value: 0.01 },
    uOoze: { value: 0 },
    uGroove: { value: 1 },
    // the shape (vertex shader); off for chips, whose geometry is already in place
    uShapeOn: { value: 0 },
    uRestR: { value: dummies.rest },
    uRestSize: { value: new T.Vector2(1, 1) },
    uImprint: { value: dummies.imprint },
    uImpSize: { value: new T.Vector2(1, 1) },
    // the finger marks' mip the vertex stage reads: about one render-mesh cell of blur (ball3d.ts sets it)
    uImpLod: { value: 1.5 },
    uDent: { value: new T.Vector3(0, 0, 1) },
    uD: { value: 0 },
    uSigma: { value: 0.4 },
    uSq: { value: new T.Vector3(0, 0, 1) },
    uSPar: { value: 1 },
    uSPerp: { value: 1 },
    uDark: { value: 0 },
    uRim: { value: 0 },
    uVol: { value: 0 },
    // the clay the moving finger drives ahead of itself: tangent direction of its travel (ball frame) × strength
    uPush: { value: new T.Vector3(0, 0, 0) },
    // how much the dent is a flat-bottomed fingertip pad (sunk into clay) rather than a soft gaussian (hard wax)
    uFlat: { value: 0 },
    // one render-mesh cell in uv (1 / segments): the vertex stage averages the relief over about that much
    uMeshCell: { value: new T.Vector2(1 / 224, 1 / 112) },
  };
}

/** Uniforms and functions both shader stages use: the cracks' state and the shell's relief. */
const SHARED = /* glsl */ `
uniform float uCrackOn;
uniform sampler2D uFieldId;
uniform sampler2D uFieldDist;
uniform sampler2D uFieldFine;
uniform sampler2D uPlateState;
uniform sampler2D uPlateFront;
uniform sampler2D uImprint;
uniform vec2 uImpSize;
uniform float uImpLod;
const float SH_THICK = ${C.SH_THICK.toFixed(4)};
const float SH_LIFT = ${C.SH_LIFT.toFixed(4)};
const float SH_PEEL = ${C.SH_PEEL.toFixed(4)};
const float SH_SINK = ${C.SH_SINK.toFixed(4)};
const float SH_FRAG = ${C.SH_FRAG.toFixed(4)};
// what the relief resolves, per stage: the fragment stage everything; the vertex stage (a mesh a few pixels per
// segment) the finger marks a little blurred (a coarser mip) and every crushed fragment at its plate's mean depth —
// a fragment's own depth steps between neighbouring vertices and would show the mesh as stair-steps
float shImpLod = 0.0;
float shFragK = 1.0;
// the finger marks' height (fraction of R). Vertex stage: the filtered high byte at a coarse mip (8 bits are plenty
// for the mesh); fragment stage: all 16 bits (R high, B low), bilinear by hand (filtering the bytes apart is wrong)
float shImpQ(int x, int y) { vec2 t = texelFetch(uImprint, ivec2(x, y), 0).rb; return t.x * 65280.0 + t.y * 255.0; }
float shImpH(vec2 uv) {
  // the high byte alone is the 16-bit value rounded down: +0.5 centres it
  if (shImpLod > 0.0) return (textureLod(uImprint, uv, shImpLod).r * 255.0 + 0.5 - 128.0) / 127.0 * ${IMPRINT_RANGE.toFixed(4)};
  ivec2 s = ivec2(uImpSize);
  vec2 p = uv * uImpSize - 0.5, f = fract(p);
  ivec2 i0 = ivec2(floor(p));
  int x0 = (i0.x % s.x + s.x) % s.x, x1 = (x0 + 1) % s.x;
  int y0 = clamp(i0.y, 0, s.y - 1), y1 = clamp(i0.y + 1, 0, s.y - 1);
  float q = mix(mix(shImpQ(x0, y0), shImpQ(x1, y0), f.x), mix(shImpQ(x0, y1), shImpQ(x1, y1), f.x), f.y);
  return (q - 32768.0) / 32767.0 * ${IMPRINT_RANGE.toFixed(4)};
}
// how far behind plate p's running crack front this texel lies (rad; <= 0: the crack has not got here)
float crBehind(vec4 f, vec3 dir) {
  vec3 o = f.rgb * 2.0 - 1.0;
  float ol = length(o);
  if (ol < 1e-3 || f.a <= 0.0) return -1.0;
  return f.a * PI - acos(clamp(dot(dir, o / ol), -1.0, 1.0));
}
// the crack at uv (vertex stage): how open it is here (0..1, front-gated), its gap half-width w (field texels) and
// whether this texel's own plate's crack front has reached it (ra); also hands back that plate (a), its first state
// texel and its front texel, which shReliefAt needs too
float shCrack(vec2 uv, vec3 dir, out float w, out float ra, out int a, out vec4 sa, out vec4 fa) {
  vec2 ids = textureLod(uFieldId, uv, 0.0).rg * 255.0;
  a = int(ids.x + 0.5);
  int b = int(ids.y + 0.5);
  sa = texelFetch(uPlateState, ivec2(a * ${STATE_TEXELS}, 0), 0);
  vec4 sb = texelFetch(uPlateState, ivec2(b * ${STATE_TEXELS}, 0), 0);
  fa = texelFetch(uPlateFront, ivec2(a, 0), 0);
  ra = step(0.0, crBehind(fa, dir));
  float rb = step(0.0, crBehind(texelFetch(uPlateFront, ivec2(b, 0), 0), dir));
  w = max(sa.a * sa.g * ra, sb.a * sb.g * rb) * (255.0 / ${GAP_SCALE.toFixed(1)});
  return max(sa.a * ra, sb.a * rb);
}
// The shell's relief at uv / unit direction dir, fraction of R. Every plate is a slab of wax SH_THICK thick on the
// clay: where its crack is open (d < w, the gap) the surface drops to the clay floor, and over ramp texels past
// the gap edge it climbs to the plate's own top — a loose plate standing proud and peeling up on the side away from
// where its crack came from (the finger), a crushed one pressed in, each fragment by its own amount. ra gates the
// plate's movement by its running crack front. The finger marks in the clay ride on top.
// a = this texel's plate, s1 its first state texel, fa its front texel (the caller has them already).
float shReliefAt(vec2 uv, vec3 dir, float d, float w, float open, float ramp, float ra, int a, vec4 s1, vec4 fa) {
  float h = shImpH(uv);
  if (uCrackOn < 0.5) return h;
  vec4 s2 = texelFetch(uPlateState, ivec2(a * ${STATE_TEXELS} + 1, 0), 0);
  vec4 s3 = texelFetch(uPlateState, ivec2(a * ${STATE_TEXELS} + 2, 0), 0);
  vec3 c = s3.rgb * 2.0 - 1.0;
  vec3 o = fa.rgb * 2.0 - 1.0;
  // the side away from where the crack came from peels up; a crack that started at the plate's centre has no such
  // side, and the plate just lifts level (as ball3d.ts plateTop, which seats the sticky chips)
  vec3 aw = c - o; aw -= c * dot(aw, c);
  aw = dot(aw, aw) > 4e-4 ? normalize(aw) : vec3(0.0);
  float tilt = abs(s1.b - 0.5) * 2.0;
  // a crushed fragment's own depth (its cell's random) on its top only: toward its edge, where the filtered cell label
  // blends texel by texel into the neighbour's, it eases to the mean (else the slots between fragments shade in streaks)
  float frag = 0.5;
  if (shFragK > 0.0) {
    vec2 fv = textureLod(uFieldFine, uv, 0.0).rg;
    float fw = s2.r * (255.0 / ${GAP_SCALE.toFixed(1)});
    frag = mix(0.5, fv.g, smoothstep(fw + 0.3, fw + 1.8, fv.r * (255.0 / ${DIST_SCALE.toFixed(1)})));
  }
  float top = ra * s3.a * (SH_LIFT + SH_PEEL * tilt * max(0.0, dot(dir - c, aw) + 0.12)) // a rigid wedge, not a fold
            - s2.b * (SH_SINK + SH_FRAG * frag);
  return h + mix(-SH_THICK * open, min(top, 0.07), smoothstep(w, w + ramp, d));
}
`;

/** The vertex shader's shape: plastic memory, the live dent and the clay it moves, the squash. */
const VERT_PARS = /* glsl */ `
uniform float uShapeOn;
uniform sampler2D uRestR;
uniform vec2 uRestSize;
uniform vec3 uDent;
uniform float uD;
uniform float uSigma;
uniform vec3 uSq;
uniform float uSPar;
uniform float uSPerp;
uniform float uDark;
uniform float uRim;
uniform float uVol;
uniform vec3 uPush;
uniform float uFlat;
uniform vec2 uMeshCell;
vec3 shP;
// the smooth shape (no relief), in view space: the fragment stage takes its screen derivatives for the relief's
// shading, since the relieved mesh is faceted (its walls are a cell wide) and would show its triangles
varying vec3 vShSmooth;
vec3 shS;
// the relief at uv as the mesh can carry it: a box average about one mesh cell wide (4 taps), so a crack wall far
// steeper than the vertex spacing becomes a ramp across a cell instead of stair-steps from vertex to vertex
float shReliefMesh(vec2 uv) {
  float acc = 0.0;
  for (int k = 0; k < 4; k++) {
    vec2 tuv = uv + vec2(k == 1 || k == 3 ? 0.35 : -0.35, k >= 2 ? 0.35 : -0.35) * uMeshCell;
    tuv.y = clamp(tuv.y, 0.0, 1.0);
    float ph = tuv.x * 2.0 * PI, th = (1.0 - tuv.y) * PI, st = sin(th);
    vec3 dir = vec3(-cos(ph) * st, cos(th), sin(ph) * st);
    float w, ra;
    int a;
    vec4 sa, fa;
    float open = shCrack(tuv, dir, w, ra, a, sa, fa);
    float dd = textureLod(uFieldDist, tuv, 0.0).r * (255.0 / ${DIST_SCALE.toFixed(1)});
    acc += shReliefAt(tuv, dir, dd, w, open * min(1.0, w / 3.0), 6.0, ra, a, sa, fa);
  }
  return 0.25 * acc;
}
// The dent's two terms at unit direction dir (ball3d.ts dentTerms is the CPU copy): g, the fingertip pad — a soft
// gaussian on hard wax, a flatter-bottomed, steeper-walled pad where the finger sinks into clay — and lip, the clay
// pushed up just outside it, heaped on the side a moving finger drives it toward and thinned behind.
void shDent(vec3 dir, out float g, out float lip) {
  float cd = clamp(dot(dir, uDent), -1.0, 1.0), ang = acos(cd);
  float s = max(uSigma, 1e-3), x = ang / s;
  g = mix(exp(-0.5 * x * x), exp(-0.5 * pow(x, 2.8)), uFlat);
  float q = (ang - ${C.RIM_RING.toFixed(3)} * s) / (${C.RIM_WIDTH.toFixed(3)} * s);
  vec3 t = dir - uDent * cd;
  float tl = length(t);
  float ahead = tl > 1e-4 ? dot(t / tl, uPush) : 0.0;
  lip = exp(-0.5 * q * q) * (1.0 + 1.4 * ahead);
}
// the smooth radius along unit direction dir: plastic memory, minus the dent under the finger, plus the lip of clay
// it pushes up just outside itself, plus the swell of the rest of the ball (more on the far side)
float shRadius(vec3 dir, out float g) {
  float phi = atan(dir.z, -dir.x);
  if (phi < 0.0) phi += 2.0 * PI;
  float th = acos(clamp(dir.y, -1.0, 1.0));
  vec2 st = (vec2(phi / (2.0 * PI), th / PI) * uRestSize + 0.5) / (uRestSize + 1.0);
  float r = 1.0 + textureLod(uRestR, st, 0.0).r;
  float lip;
  shDent(dir, g, lip);
  // the swell leans away from the eased squash axis (not the dent), so it shifts smoothly when the finger moves
  return r * (1.0 - uD * g + uD * uRim * lip + uVol * (1.0 - 0.5 * clamp(dot(dir, uSq), -1.0, 1.0)));
}
`;

const VERT_SHAPE = /* glsl */ `
shP = position;
shS = position;
shImpLod = uImpLod; shFragK = 0.0;
if (uShapeOn > 0.5) {
  vec3 shDir = normalize(position);
  // the pole vertices are one point with many uvs: give them all one, or the relief tears the pole fan
  vec2 shUv = abs(shDir.y) > 0.99999 ? vec2(0.0, uv.y) : uv;
  float shG, shG2;
  float shR0 = shRadius(shDir, shG);
  // the smooth shape's normal from central differences about a shape-grid cell wide (narrower would pick up the
  // bilinear facets of the coarse grid as ripples in the reflections); the relief's own slopes are shaded per texel
  vec3 shUp = abs(shDir.y) < 0.99 ? vec3(0.0, 1.0, 0.0) : vec3(1.0, 0.0, 0.0);
  vec3 shT1 = normalize(cross(shUp, shDir)), shT2 = cross(shDir, shT1);
  float shE = 1.6 * PI / uRestSize.y;
  vec3 shA1 = normalize(shDir + shT1 * shE), shB1 = normalize(shDir - shT1 * shE);
  vec3 shA2 = normalize(shDir + shT2 * shE), shB2 = normalize(shDir - shT2 * shE);
  vec3 shN = normalize(cross(shA1 * shRadius(shA1, shG2) - shB1 * shRadius(shB1, shG2), shA2 * shRadius(shA2, shG2) - shB2 * shRadius(shB2, shG2)));
  if (dot(shN, shDir) < 0.0) shN = -shN;
  // the relief, with a wide ramp and averaged over a mesh cell: the mesh is coarser than the field, and a narrow
  // crack only dips a little here (the fragment shader draws its sharp wall)
  vec3 shQ = shDir * (shR0 + shReliefMesh(shUv));
  // the squash is one-sided: the finger's side flattens, the far side stays where it is, the middle widens
  float shAl = dot(shQ, uSq);
  shP = uSq * ((shAl + 1.0) * uSPar - 1.0) + (shQ - uSq * shAl) * uSPerp;
  vec3 shQ0 = shDir * shR0;
  float shAl0 = dot(shQ0, uSq);
  shS = uSq * ((shAl0 + 1.0) * uSPar - 1.0) + (shQ0 - uSq * shAl0) * uSPerp;
  float shAn = dot(shN, uSq);
  objectNormal = normalize(uSq * shAn / uSPar + (shN - uSq * shAn) / uSPerp);
  #ifdef USE_COLOR
  vColor.rgb *= 1.0 - uDark * shG;
  #endif
}
`;

const PARS = /* glsl */ `
varying vec3 vShSmooth;
uniform sampler2D uHole;
uniform vec3 uClay;
uniform vec3 uMudColor;
uniform float uMud;
uniform vec3 uEdge;
uniform vec3 uHair;
uniform vec3 uWax;
uniform float uTexelWorld;
uniform float uOoze;
uniform float uGroove;
float crWet = 0.0;   // freshly pressed clay (smoothed by the finger, a little shinier)
float crGap = 0.0;   // 1 inside an open crack (clay shows)
float crEdge = 0.0;  // the whitened fracture edge of the wax along an open crack
float crHeight = 0.0; // groove depth (field texels, negative = into the ball) for the lighting
float waxGloss = 1.0; // the wax's gloss varies over the surface (dip lines glossier, pits duller): roughness × this
vec3 crWallN = vec3(0.0, 0.0, 1.0); // the side wall of this plate's slab, facing the crack (view space)…
float crWallK = 0.0;                // …and where that wall is seen (looking into a crack at an angle)
float crAO = 1.0;    // how much of the sky the clay floor of a slot sees between its two walls
// the slot this clay pixel lies in, for the direct light's contact shadow (RE_Direct_Wax): the plates' crack and
// the finer slots between crushed fragments — across-slot direction (view space), distances to the two walls and
// their height (world units), and how much each applies here
vec3 crSlotG = vec3(0.0); float crSlotN = 0.0, crSlotF = 0.0, crSlotH = 0.0, crSlotK = 0.0;
vec3 crFSlotG = vec3(0.0); float crFSlotN = 0.0, crFSlotF = 0.0, crFSlotH = 0.0, crFSlotK = 0.0;
vec3 crNrm0 = vec3(0.0, 0.0, 1.0); // the smooth surface normal (view space)
// what the sky above a slot of depth h looks like from its floor, dn and df from the two walls (2D: the walls run on)
float crSlotSky(float dn, float df, float h) {
  return mix(1.0, sqrt((atan(dn / h) + atan(df / h)) / PI), 0.6);
}
// the share of a light from direction l that gets down to the floor past the slot's walls
float crSlotLit(vec3 l, vec3 g, float dn, float df, float h) {
  float ln = max(dot(l, crNrm0), 0.02);
  vec3 lt = l - crNrm0 * dot(l, crNrm0);
  float a = dot(lt, g);
  float dist = a > 0.0 ? dn : df;
  float reach = dist * ln / max(abs(a), 1e-4); // the ray's height where it passes the wall
  return mix(0.35, 1.0, smoothstep(0.3, 1.2, reach / max(h, 1e-6)));
}
// 3D value noise on the ball's own direction: the clay's soft lumps, round wherever they are (noise over the uv
// map would stretch into streaks toward the poles). An integer hash: sin() of large cell numbers loses its precision
// on phones, and fract()-based hashes of whole cell numbers come out in diagonal stripes.
float crHash(vec3 c) {
  uvec3 q = uvec3(ivec3(c) + 4096);
  uint h = (q.x * 1597334677u) ^ (q.y * 3812015801u) ^ (q.z * 2798796415u);
  h ^= h >> 16; h *= 2246822519u; h ^= h >> 13; h *= 3266489917u; h ^= h >> 16;
  return float(h) * (1.0 / 4294967296.0);
}
float crNoise(vec3 p) {
  vec3 i = floor(p), f = p - i, u = f * f * (3.0 - 2.0 * f);
  float a = crHash(i), b = crHash(i + vec3(1.0, 0.0, 0.0)), c = crHash(i + vec3(0.0, 1.0, 0.0)), d = crHash(i + vec3(1.0, 1.0, 0.0));
  float e = crHash(i + vec3(0.0, 0.0, 1.0)), g = crHash(i + vec3(1.0, 0.0, 1.0)), k = crHash(i + vec3(0.0, 1.0, 1.0)), l = crHash(i + vec3(1.0, 1.0, 1.0));
  return mix(mix(mix(a, b, u.x), mix(c, d, u.x), u.y), mix(mix(e, g, u.x), mix(k, l, u.x), u.y), u.z);
}
// height -> normal: n' = normalize(n - grad h), with grad h taken from screen derivatives
vec3 crPerturb(vec3 surfPos, vec3 n, float h) {
  vec3 px = dFdx(surfPos), py = dFdy(surfPos);
  float lx = max(length(px), 1e-6), ly = max(length(py), 1e-6);
  vec3 g = (dFdx(h) / lx) * (px / lx) + (dFdy(h) / ly) * (py / ly);
  g -= n * dot(g, n);
  return normalize(n - g);
}
`;

const MAIN = /* glsl */ `
if (uCrackOn > 0.5) {
  vec2 crIds = texture2D(uFieldId, vMapUv).rg * 255.0;
  int crIa = int(crIds.x + 0.5), crIb = int(crIds.y + 0.5);
  vec4 crA = texelFetch(uPlateState, ivec2(crIa * ${STATE_TEXELS}, 0), 0);
  vec4 crB = texelFetch(uPlateState, ivec2(crIb * ${STATE_TEXELS}, 0), 0);
  vec4 crA2 = texelFetch(uPlateState, ivec2(crIa * ${STATE_TEXELS} + 1, 0), 0); // this texel's plate: its fragments
  float crLiftA = texelFetch(uPlateState, ivec2(crIa * ${STATE_TEXELS} + 2, 0), 0).a;
  float crLiftB = texelFetch(uPlateState, ivec2(crIb * ${STATE_TEXELS} + 2, 0), 0).a;
  float crD = texture2D(uFieldDist, vMapUv).r * (255.0 / ${DIST_SCALE.toFixed(1)});
  float crAA = max(fwidth(crD) * 0.8, 0.03);
  // this texel's direction on the unit ball (same convention as plateField.ts)
  float crPhi = vMapUv.x * 2.0 * PI, crTh = (1.0 - vMapUv.y) * PI, crSt = sin(crTh);
  vec3 crDir = vec3(-cos(crPhi) * crSt, cos(crTh), sin(crPhi) * crSt);
  vec4 crFa = texelFetch(uPlateFront, ivec2(crIa, 0), 0);
  float crBa = crBehind(crFa, crDir);
  float crBb = crBehind(texelFetch(uPlateFront, ivec2(crIb, 0), 0), crDir);
  // reached: the front has passed here; tip: the gap narrows to a point over the last stretch behind the front
  float crRa = smoothstep(0.0, ${FRONT_SOFT.toFixed(3)}, crBa), crRb = smoothstep(0.0, ${FRONT_SOFT.toFixed(3)}, crBb);
  float crTa = clamp(crBa / ${TIP_LEN.toFixed(3)}, 0.0, 1.0), crTb = clamp(crBb / ${TIP_LEN.toFixed(3)}, 0.0, 1.0);
  // a crack opens symmetrically about the border if either side is cracked (and its front has reached this point)
  float crOpen = max(crA.a * crRa, crB.a * crRb);
  float crW = max(crA.a * crA.g * crTa, crB.a * crB.g * crTb) * (255.0 / ${GAP_SCALE.toFixed(1)});
  // the wax has thickness: past the gap a lip of broken wax shows, hairline-thin on a fresh crack, wider open
  float crEw = 0.4 + 0.3 * crW;
  // The crack has depth. Where the surface is open, the eye's ray goes down into the gap and meets the clay floor
  // SH_THICK (plus the plate's lift) below, shifted along the view's tangent direction: that shifted point is what
  // this pixel shows. Its distance from the border, crD2, says whether it is still floor (|crD2| < w) or the ray
  // hit the gap's far wall first (the slab's cut side, lit as a wall). The floor's own detail is read at the shifted
  // spot too, so it slides against the wax as the ball turns, the way a real slot's bottom does.
  vec3 crVv = normalize(vViewPosition), crNv = normalize(vNormal);
  crNrm0 = crNv;
  // screen derivatives of the smooth surface span its tangent plane; the 2×2 solve below turns a tangent vector or a
  // per-pixel gradient into that basis (the two derivatives are not orthogonal away from the ball's centre)
  vec3 crQx = dFdx(vShSmooth), crQy = dFdy(vShSmooth);
  float crA11 = dot(crQx, crQx), crA12 = dot(crQx, crQy), crA22 = dot(crQy, crQy);
  float crDet = crA11 * crA22 - crA12 * crA12;
  float crInv = abs(crDet) > 1e-18 ? 1.0 / crDet : 0.0;
  #define CR_SOLVE(b1, b2) (vec2(crA22 * (b1) - crA12 * (b2), crA11 * (b2) - crA12 * (b1)) * crInv)
  vec2 crGab = CR_SOLVE(dFdx(crD), dFdy(crD));
  vec3 crGv = crGab.x * crQx + crGab.y * crQy; // ∇d, field texels per world unit (tangent plane)
  float crGl = length(crGv);
  vec3 crGdir = crGl > 1e-9 ? crGv / crGl : vec3(0.0);
  float crRw = uTexelWorld * ${(512 / Math.PI).toFixed(3)}; // the ball's radius, world units
  float crDepth = (SH_THICK + SH_LIFT * max(crLiftA, crLiftB)) * crRw; // the floor below the plate tops (world)
  float crVn = max(dot(crVv, crNv), 0.12);
  vec3 crOff = -(crVv - crNv * dot(crVv, crNv)) / crVn * crDepth; // surface point → floor point the ray reaches
  float crDd = dot(crOff, crGv); // field texels
  float crD2 = crD + crDd;
  // the same shift in the map, for the floor's detail (the u derivative taken across the seam, u = 1 → 0, unwrapped)
  vec2 crAb = CR_SOLVE(dot(crOff, crQx), dot(crOff, crQy));
  vec2 crDux = dFdx(vMapUv), crDuy = dFdy(vMapUv);
  crDux.x -= round(crDux.x); crDuy.x -= round(crDuy.x);
  vec2 crUv2 = vMapUv + crAb.x * crDux + crAb.y * crDuy;
  float crSurf = crOpen * (1.0 - smoothstep(crW - crAA, crW + crAA, crD)); // the surface is open here
  float crFloor = 1.0 - smoothstep(crW - crAA, crW + crAA, abs(crD2));       // the ray reaches the floor
  float crIn = crSurf * crFloor;
  float crE = crOpen * (1.0 - smoothstep(crW + crEw - crAA, crW + crEw + crAA, crD)) * (1.0 - crSurf);
  crWallK = crSurf * (1.0 - crFloor);
  // the wall the ray hit faces back across the gap; how far up it the ray struck, for its layered colour
  crWallN = crGl > 1e-9 ? -sign(crDd + 1e-6) * crGdir : crNv;
  float crToWall = crDd > 0.0 ? crW - crD : crW + crD; // texels the ray covers before the wall it hits
  float crWallUp = 1.0 - clamp(crToWall / max(abs(crDd), 1e-4), 0.0, 1.0);
  // the slot's geometry for the lighting: distances from the floor point to its two walls, world units
  float crDn = max(crW - abs(crD2), 0.0) * uTexelWorld, crDf = (crW + abs(crD2)) * uTexelWorld;
  crSlotG = crD2 >= 0.0 ? crGdir : -crGdir; crSlotN = crDn; crSlotF = crDf; crSlotH = crDepth; crSlotK = crIn;
  // hairline: before the shell opens, a fine line along the plate border (never thinner than ~1 px)
  float crHair = max(crA.r * crRa, crB.r * crRb) * (1.0 - crOpen);
  float crHw = max(0.42, crAA * 1.1);
  crHair *= (1.0 - smoothstep(crHw - crAA, crHw + crAA, crD)) * clamp(0.7 / max(crAA * 2.0, 0.35), 0.35, 1.0);
  // a crushed plate shatters along the finer layer: hairlines first, then its fragments part on the clay
  // (read at the floor point where the plate crack is open: the fragments sit on the clay, below the plates)
  vec2 crUvF = mix(vMapUv, crUv2, crIn);
  vec2 crFv = texture2D(uFieldFine, crUvF).rg;
  float crFd = crFv.r * (255.0 / ${DIST_SCALE.toFixed(1)});
  float crFr = crFv.g; // this fragment's own random: how far it is tipped and pressed in (assigned below, off its edge)
  float crFaa = max(fwidth(crFd) * 0.8, 0.03);
  float crFw = crA2.r * (255.0 / ${GAP_SCALE.toFixed(1)});
  crFr = mix(0.5, crFr, smoothstep(crFw + 0.3, crFw + 1.8, crFd)); // the cell label blends texel by texel at the edge
  float crFon = crA2.a * (1.0 - crIn);
  float crFin = crFon * step(0.02, crFw) * (1.0 - smoothstep(crFw - crFaa, crFw + crFaa, crFd));
  float crFew = 0.3 + 0.3 * crFw;
  float crFe = crFon * step(0.02, crFw) * (1.0 - smoothstep(crFw + crFew - crFaa, crFw + crFew + crFaa, crFd)) * (1.0 - crFin);
  float crFhw = max(0.38, crFaa * 1.1);
  float crFhair = crFon * crA2.g * (1.0 - smoothstep(crFhw - crFaa, crFhw + crFaa, crFd)) * (1.0 - crFin) * (1.0 - smoothstep(0.3, 0.9, crFw));
  crHair = max(crHair, crFhair * 0.25);
  // peeled-chip holes (soft-edged mask)
  float crHv = texture2D(uHole, vMapUv).r;
  float crHaa = max(fwidth(crHv), 0.004);
  float crInHole = smoothstep(0.5 - crHaa, 0.5 + crHaa, crHv);
  float crHoleRim = smoothstep(0.34 - crHaa, 0.34 + crHaa, crHv) * (1.0 - crInHole);
  crGap = max(max(crIn, crInHole), crFin);
  crEdge = max(max(max(crE, crWallK), crHoleRim), crFe);
  // the finer slots' geometry for the lighting (their floor is shallower: the fragments are pressed part-way in)
  vec2 crFGab = CR_SOLVE(dFdx(crFd), dFdy(crFd));
  vec3 crFGv = crFGab.x * crQx + crFGab.y * crQy;
  float crFGl = length(crFGv);
  crFSlotG = crFGl > 1e-9 ? crFGv / crFGl : vec3(0.0);
  crFSlotN = max(crFw - crFd, 0.0) * uTexelWorld; crFSlotF = (crFw + crFd) * uTexelWorld;
  crFSlotH = (SH_SINK + 0.5 * SH_FRAG) * crA2.b * crRw + 0.25 * SH_THICK * crRw; crFSlotK = crFin * (1.0 - crIn);
  // what the floor sees of the sky between its walls: the ambient light it gets
  crAO = mix(1.0, crSlotSky(crDn, crDf, crDepth), crIn);
  crAO *= mix(1.0, crSlotSky(crFSlotN, crFSlotF, crFSlotH), crFSlotK);
  crAO = mix(crAO, 0.8, crInHole * (1.0 - crIn));
  // clay is soft and lumpy: a low, irregular relief wherever it shows, with a fine grain that only comes in where a
  // cell of it is more than a pixel or so on screen (the big ball, up close), so it never shimmers
  // (only where clay shows: crLump is only ever used times crGap, and the noise takes no derivatives)
  float crGrainK = 1.0 - smoothstep(0.35, 0.8, length(fwidth(crDir)) * 255.0);
  float crLump = 0.0;
  if (crGap > 0.0) {
    // on the floor point the ray reached (the lumps slide under the wax edge with the view)
    float crPhi2 = crUvF.x * 2.0 * PI, crTh2 = (1.0 - clamp(crUvF.y, 0.0, 1.0)) * PI, crSt2 = sin(crTh2);
    vec3 crDir2 = vec3(-cos(crPhi2) * crSt2, cos(crTh2), sin(crPhi2) * crSt2);
    crLump = crNoise(crDir2 * 41.0) * 0.6 + crNoise(crDir2 * 102.0) * 0.3
           + (crGrainK > 0.0 ? (crNoise(crDir2 * 255.0) - 0.5) * 0.28 * crGrainK : 0.0);
  }
  // height (field texels): the slabs and their crack floors (shReliefAt), deep slots between crushed fragments with
  // rounded tops, hairlines as fine scratches, peeled holes, the clay's lumps
  crHeight = shReliefAt(vMapUv, crDir, crD, crW, crOpen, crEw, crRa, crIa, crA, crFa) * ${(512 / Math.PI).toFixed(3)}
           - crFon * step(0.02, crFw) * (1.0 - smoothstep(crFw, crFw + crFew, crFd)) * (2.0 + 0.5 * crFw)
           + crFon * crA2.b * 1.2 * smoothstep(crFw, crFw + 3.0, crFd)
           - crHair * 1.0 - crInHole * 1.2
           + crGap * crLump;
  // cracked plates tilt a little (loose ones more), catching the light differently
  float crTilt = (crA.b - 0.5) * 2.0 * crA.a * crRa;
  // pressed-in fragments sit in their own shadow, each a little differently (they tipped as they broke)
  diffuseColor.rgb *= (1.0 + crTilt * 0.08) * (1.0 - 0.1 * crA2.b) * (1.0 + crFon * crA2.b * (crFr - 0.5) * 0.34);
  vec3 crClay = mix(uClay, uMudColor, uMud);
  float crT = crW > 0.0 ? clamp(abs(crD2) / max(crW, 1e-3), 0.0, 1.0) : 1.0;
  // a fresh crack is a dark slit; a wide-open one shows the coloured core itself, shadowed under the wax wall;
  // a narrow slot gets little light down to its floor
  vec3 crNarrow = crClay * mix(0.45, 0.7 + 0.2 * uOoze, pow(crT, 1.6));
  vec3 crWideCol = crClay * (0.98 - 0.25 * smoothstep(0.55, 1.0, crT));
  vec3 crGapCol = mix(crNarrow, crWideCol, smoothstep(1.6, 4.0, crW)) * mix(0.8, 1.0, smoothstep(1.5, 5.0, crW));
  // between the fragments: the same, on the fragments' smaller scale
  float crFt = crFw > 0.0 ? clamp(crFd / max(crFw, 1e-3), 0.0, 1.0) : 1.0;
  vec3 crFineCol = crClay * mix(mix(0.4, 0.7, pow(crFt, 1.5)), 0.95 - 0.25 * smoothstep(0.5, 1.0, crFt), smoothstep(0.8, 2.2, crFw));
  crGapCol = mix(crGapCol, crFineCol, crFin * (1.0 - crIn));
  crGapCol = mix(crGapCol, crClay * (0.62 + 0.2 * uOoze), crInHole);
  crGapCol *= 0.86 + 0.22 * crLump;
  // clay the finger has pressed is smoothed and a touch darker
  crWet = textureLod(uImprint, crUvF, 0.0).g;
  crGapCol *= 1.0 - 0.12 * crWet;
  // a plate standing proud of the clay shades the gap under its edge
  crGapCol *= 1.0 - 0.25 * max(crLiftA, crLiftB) * (1.0 - 0.5 * crT);
  // the wall: a layered cut through the shell — the pale inner wax at the bottom (in shadow where it meets
  // the clay), the coloured coat on top with a catch-light on its lip
  float crWallT = crFe > max(crE, crWallK) ? clamp((crFd - crFw) / max(crFew, 1e-3), 0.0, 1.0)
                : crWallK > crE ? crWallUp : clamp((crD - crW) / max(crEw, 1e-3), 0.0, 1.0);
  vec3 crInner = uWax * mix(0.5, 0.92, smoothstep(0.0, 0.5, crWallT));
  vec3 crCoat = mix(diffuseColor.rgb * 0.95, uEdge, 0.25);
  vec3 crWallCol = mix(crInner, crCoat, smoothstep(0.42, 0.62, crWallT));
  diffuseColor.rgb = mix(diffuseColor.rgb, uHair, crHair * 0.35);
  diffuseColor.rgb = mix(diffuseColor.rgb, crWallCol, crEdge);
  diffuseColor.rgb = mix(diffuseColor.rgb, crGapCol, crGap);
}
`;

/**
 * Wax is a little translucent: light that goes into the coat scatters in the dye and comes back out a short way off.
 * So the diffuse wraps past the shadow line, in a deeper, more saturated colour (the light travelled through more
 * dye), and the rim glows faintly where the coat is seen edge-on. Clay (open cracks) is opaque: it only wraps a
 * little and takes no glow. Specular, clearcoat and sheen are three's own (RE_Direct_Physical).
 */
const WAX_LIGHT = /* glsl */ `
void RE_Direct_Wax(const in IncidentLight directLight, const in vec3 geometryPosition, const in vec3 geometryNormal, const in vec3 geometryViewDir, const in vec3 geometryClearcoatNormal, const in PhysicalMaterial material, inout ReflectedLight reflectedLight) {
  ReflectedLight wxL = ReflectedLight(vec3(0.0), vec3(0.0), vec3(0.0), vec3(0.0));
  RE_Direct_Physical(directLight, geometryPosition, geometryNormal, geometryViewDir, geometryClearcoatNormal, material, wxL);
  // down in a slot, the walls shade the floor from a light that comes in low across them
  float slotLit = mix(1.0, crSlotLit(directLight.direction, crSlotG, crSlotN, crSlotF, crSlotH), crSlotK)
                * mix(1.0, crSlotLit(directLight.direction, crFSlotG, crFSlotN, crFSlotF, crFSlotH), crFSlotK);
  reflectedLight.directSpecular += wxL.directSpecular * slotLit;
  float wax = 1.0 - crGap;
  float nl = dot(geometryNormal, directLight.direction);
  float w = mix(0.12, 0.6, wax);
  float lam = saturate(nl), wrap = saturate((nl + w) / (1.0 + w));
  vec3 c = material.diffuseColor;
  vec3 deep = mix(c, c * c / max(max(max(c.r, c.g), c.b), 0.04), 0.7 * wax);
  float rim = pow(1.0 - saturate(dot(geometryNormal, geometryViewDir)), 3.0) * saturate(nl + 0.6) * 0.3 * wax;
  // the wrapped light is borrowed from the lit side (it went in there), so the lit side gives a little of it back
  reflectedLight.directDiffuse += directLight.color * slotLit * (BRDF_Lambert(c) * lam * (1.0 - 0.12 * wax) + BRDF_Lambert(deep) * (0.8 * (wrap - lam) + rim));
}
#undef RE_Direct
#define RE_Direct RE_Direct_Wax
`;

/**
 * A ball's material for the desktop AO pass's normal + depth buffer: a MeshNormalMaterial running the same vertex
 * shape on the ball's own uniforms (`crack`), so the occlusion sees the dents, the squash and the shell's relief.
 * One per ball (three uploads a material's uniforms once per material per render, so a shared one would draw every
 * ball with the first ball's shape); they all share one program.
 */
export function makeAoShapeMaterial(T: ThreeNS, crack: CrackUniforms) {
  const m = new T.MeshNormalMaterial();
  m.blending = T.NoBlending;
  m.crackUniforms = crack;
  m.onBeforeCompile = aoOnBeforeCompile;
  return m;
}

/** The vertex half of crackOnBeforeCompile alone: the normal material's fragment stage has none of the crack code's hooks. */
function aoOnBeforeCompile(this: { crackUniforms?: CrackUniforms }, shader: { uniforms: Record<string, unknown>; vertexShader: string }) {
  if (this.crackUniforms) Object.assign(shader.uniforms, this.crackUniforms);
  shader.vertexShader = shapeVertex(shader.vertexShader);
}

/** Injects the ball's shape into a vertex shader (any material with the standard begin/beginnormal chunks). */
function shapeVertex(vs: string) {
  vs = vs.replace("#include <common>", "#include <common>\n" + SHARED + VERT_PARS);
  vs = vs.replace("#include <beginnormal_vertex>", "#include <beginnormal_vertex>\n" + VERT_SHAPE);
  vs = vs.replace("#include <begin_vertex>", "#include <begin_vertex>\ntransformed = shP;\nvShSmooth = (modelViewMatrix * vec4(shS, 1.0)).xyz;");
  return vs;
}

/** onBeforeCompile for the ball and chip materials. `this` is the material; its uniforms live on `crackUniforms`. */
export function crackOnBeforeCompile(this: { crackUniforms?: CrackUniforms }, shader: { uniforms: Record<string, unknown>; vertexShader: string; fragmentShader: string }) {
  if (this.crackUniforms) Object.assign(shader.uniforms, this.crackUniforms);
  shader.vertexShader = shapeVertex(shader.vertexShader);
  let fs = shader.fragmentShader;
  fs = fs.replace("#include <common>", "#include <common>\n" + SHARED + PARS);
  fs = fs.replace("#include <map_fragment>", "#include <map_fragment>\n" + MAIN);
  fs = fs.replace("#include <lights_physical_pars_fragment>", "#include <lights_physical_pars_fragment>\n" + WAX_LIGHT);
  fs = fs.replace("#include <aomap_fragment>", "#include <aomap_fragment>\nreflectedLight.indirectDiffuse *= crAO;\nreflectedLight.indirectSpecular *= crAO;\n#ifdef USE_SHEEN\nsheenSpecularIndirect *= crAO;\n#endif");
  fs = fs.replace(
    "#include <roughnessmap_fragment>",
    "#include <roughnessmap_fragment>\n#ifdef USE_NORMALMAP\nwaxGloss = 1.0 + 0.24 * (texture2D(normalMap, vNormalMapUv).a * 2.0 - 1.0);\n#endif\nroughnessFactor *= waxGloss;\nroughnessFactor = mix(roughnessFactor, 0.6, crEdge);\nroughnessFactor = mix(roughnessFactor, 0.94 - 0.3 * crWet, crGap);",
  );
  fs = fs.replace("#include <metalnessmap_fragment>", "#include <metalnessmap_fragment>\nmetalnessFactor *= 1.0 - max(crGap, crEdge * 0.7);");
  fs = fs.replace("#include <normal_fragment_maps>", "#include <normal_fragment_maps>\nif (uCrackOn > 0.5) { normal = normalize(mix(normal, nonPerturbedNormal, max(crGap, 0.6 * crEdge))); normal = crPerturb(vShSmooth, normal, crHeight * uTexelWorld * uGroove); normal = normalize(mix(normal, crWallN, 0.7 * crWallK)); }");
  fs = fs.replace(
    "#include <clearcoat_normal_fragment_maps>",
    "#include <clearcoat_normal_fragment_maps>\n#ifdef USE_CLEARCOAT\nif (uCrackOn > 0.5) clearcoatNormal = crPerturb(vShSmooth, clearcoatNormal, crHeight * uTexelWorld * uGroove);\n#endif",
  );
  fs = fs.replace(
    "#include <lights_physical_fragment>",
    "#include <lights_physical_fragment>\n#ifdef USE_CLEARCOAT\nmaterial.clearcoat *= (1.0 - crGap) * (1.0 - 0.6 * crEdge);\nmaterial.clearcoatRoughness = min(1.0, material.clearcoatRoughness * mix(waxGloss, 1.0, crGap));\n#endif\n#ifdef USE_SHEEN\nmaterial.sheenColor *= 1.0 - crGap;\n#endif",
  );
  shader.fragmentShader = fs;
}
